import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { ConnectionState, RoomEvent, type RemoteParticipant } from 'livekit-client'
import { useAuth } from '@/state/AuthContext'
import { apiFailure } from '@/lib/apiFailure'
import { watchApi } from './api'
import { RETRY_JITTER_MS, retryDelayMs } from './reconnect'
import { CHAT_MAX_MESSAGES, chatBlock, chatBody, isChatCard, normalizeChatBody } from './rules'
import type { ChatBlocked, ChatCardKind, ChatEvent, ChatGif, ChatMessage, ChatPerson, StageBan, StageBanKind, StagePayload } from './types'

/** The longest wait before history is fetched again after a reconnect, so a room reconnecting together spreads out. */
export const RECONNECT_JITTER_MS = RETRY_JITTER_MS

/**
 * How far a stage's memory may outgrow the messages shown before it is pruned again: a long stream's packets and
 * posts otherwise leave an entry each in `seen` and `arrived`.
 */
export const STAGE_MEMORY_SLACK = 50

/** How much later than the server's `created_at` a pending message may be stamped (the client's clock) and still be it. */
const PENDING_MATCH_SLACK_MS = 60_000

/** setTimeout's longest delay. */
const MAX_TIMER_MS = 2 ** 31 - 1

/** Why a send needs the viewer to sign in again: the session is gone (401), or the account has no username yet (422). */
export type SignInReason = 'signed_out' | 'username_required'

/** What the chat reads from the stage payload's `viewer`. */
export type StageChatViewer = Pick<NonNullable<StagePayload['viewer']>, 'chat_blocked_until' | 'chat_removed'>

/** A ban as `ban` resolves it: the API's answer and the stage it was made on, which Undo passes back to `unban`. */
export type StageChatBan = StageBan & { stage_id: string }

export interface StageChatOptions {
  /** The chat is on screen. Messages that arrive while it isn't count as unread. */
  active: boolean
  /** A send needs sign-in (or a username) first. The draft stays, so the intent can send it afterwards. */
  onSignInNeeded: (reason: SignInReason) => void
  /**
   * The stage payload's `viewer` (null signed out). Its `chat_blocked_until` and `chat_removed` seed `blocked`, again
   * whenever the object changes: pass the payload's object as fetched, so a reload (after a host's Undo, say) seeds it
   * again and an ordinary render doesn't.
   */
  viewer?: StageChatViewer | null
}

export interface StageChat {
  /** Confirmed messages by `created_at`, then `id` (the newest 200), then this tab's pending and failed ones. */
  messages: ChatMessage[]
  /** True until the stage's first history answer (or failure), and while `reloadHistory` runs before any has loaded. */
  loading: boolean
  /** No history page has loaded for this stage yet and the last request failed. It retries by itself meanwhile. */
  historyError: boolean
  /** Asks for history again at once (the panel's retry after a failure). */
  reloadHistory: () => void
  draft: string
  setDraft: (draft: string) => void
  /** Posts `body`, or the draft. Resolves true once the server has the message. */
  send: (body?: string) => Promise<boolean>
  /** Posts a GIF on its own (the draft stays). Resolves true once the server has the message. */
  sendGif: (gif: ChatGif) => Promise<boolean>
  /** Posts a failed message again. */
  retry: (message: ChatMessage) => Promise<boolean>
  /** This viewer's timeout (until a time) or removal (no end) from this stage's chat, or null. A timeout clears itself. */
  blocked: ChatBlocked | null
  /** Messages that arrived while the chat wasn't active and are still shown. */
  unread: number
  /** Hosts: deletes a message. It disappears at once and comes back if the delete fails (the promise rejects). */
  remove: (messageId: string) => Promise<void>
  /** Hosts: times out or removes someone, hiding their messages (not their cards) at once; they come back if it fails (the promise rejects). */
  ban: (userId: string, kind: StageBanKind) => Promise<StageChatBan>
  /** Hosts: undoes a timeout or removal on `stageId` (the ban's stage; this stage by default). Hidden messages stay hidden, as on the server. */
  unban: (userId: string, stageId?: string) => Promise<void>
}

const EMPTY: ChatMessage[] = []

const draftKey = (stageId: string) => `sb_chat_draft:${stageId}`

function readDraft(stageId: string) {
  try {
    return window.sessionStorage.getItem(draftKey(stageId)) ?? ''
  } catch {
    return ''
  }
}

function writeDraft(stageId: string, text: string) {
  try {
    if (text) window.sessionStorage.setItem(draftKey(stageId), text)
    else window.sessionStorage.removeItem(draftKey(stageId))
  } catch {
    // Private mode: the draft lives in memory only.
  }
}

const CARD_KINDS: ReadonlySet<unknown> = new Set<ChatCardKind>(['invite', 'joined', 'left'])

const isLocal = (message: ChatMessage) => Boolean(message.pending || message.failed)
/** A row a server message can still confirm: pending, or failed when the post may have been saved anyway. */
const confirmable = (message: ChatMessage) => Boolean(message.pending || (message.failed && message.uncertain))
const time = (message: ChatMessage) => Date.parse(message.created_at) || 0
/** Two rows carry the same GIF, or both none. */
const sameGif = (a: ChatMessage, b: ChatMessage) => (a.gif?.id ?? null) === (b.gif?.id ?? null)
/**
 * The order shown: `created_at`, then `id`, so whatever order messages arrive in, they show in one order. The server
 * orders by its microsecond `created_at` and the payload carries milliseconds, so within one millisecond the server's
 * order (its pages, `before`) can differ from this one.
 */
const byTimeThenId = (a: ChatMessage, b: ChatMessage) => time(a) - time(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/** Ids that must not show: removed by the server, or hidden by this tab's moderation. */
type Excluded = { has: (id: string) => boolean }
const NONE: Excluded = { has: () => false }

/**
 * Merges messages by id, leaving out `excluded` ones: confirmed ones by `created_at` then `id`, keeping the newest 200,
 * then this tab's pending and failed ones in the order they were sent. A message that replaces a confirmed copy keeps
 * that copy's `localId`.
 */
function mergeMessages(current: ChatMessage[], incoming: ChatMessage[], excluded: Excluded = NONE): ChatMessage[] {
  const byId = new Map(current.map((message) => [message.id, message]))
  let changed = false
  for (const message of incoming) {
    if (excluded.has(message.id)) continue
    const localId = message.localId ?? byId.get(message.id)?.localId
    byId.set(message.id, localId ? { ...message, localId } : message)
    changed = true
  }
  if (!changed) return current
  const all = Array.from(byId.values())
  const confirmed = all.filter((message) => !isLocal(message)).sort(byTimeThenId)
  return [...confirmed.slice(-CHAT_MAX_MESSAGES), ...all.filter(isLocal)]
}

/**
 * Pairs each incoming server message that isn't shown yet with the row of this tab's it confirms: the oldest pending
 * row, or failed row whose post may have been saved, by the same author with the same body (as the server saves it),
 * stamped no later than the server's time plus slack, and not claimed by another message. The server message takes
 * the row's place and its id as `localId` (the row's key). Returns the list without the claimed rows, the incoming
 * messages, and the bodies of the failed rows claimed (sent after all, so a draft holding one can empty). A card never
 * claims a row: the server posts it, not this tab.
 */
function claimPending(list: ChatMessage[], incoming: ChatMessage[]) {
  const sentAfterAll: string[] = []
  if (!list.some(confirmable)) return { list, incoming, sentAfterAll }
  const shown = new Set(list.map((m) => m.id))
  const taken = new Set(list.flatMap((m) => (!isLocal(m) && m.localId ? [m.localId] : [])))
  const claimed = new Set<string>()
  const paired = [...incoming].sort(byTimeThenId).map((message) => {
    // A card is the server's, never a message this tab sent.
    if (shown.has(message.id) || message.localId || isChatCard(message)) return message
    const body = normalizeChatBody(message.body)
    const latest = time(message) + PENDING_MATCH_SLACK_MS
    const row = list.find(
      (m) =>
        confirmable(m) && !taken.has(m.id) && !claimed.has(m.id) && m.user.id === message.user.id && m.body === body && sameGif(m, message) && time(m) <= latest,
    )
    if (!row) return message
    claimed.add(row.id)
    if (row.failed && row.body) sentAfterAll.push(row.body)
    return { ...message, localId: row.id }
  })
  return { list: claimed.size ? list.filter((m) => !claimed.has(m.id)) : list, incoming: paired, sentAfterAll }
}

/** A GIF as the API stores it: an id and an https URL to show (anything else is left off the message). */
export function isChatGif(value: unknown): value is ChatGif {
  if (!value || typeof value !== 'object') return false
  const gif = value as Partial<ChatGif>
  return typeof gif.id === 'string' && typeof gif.url === 'string' && gif.url.startsWith('https://')
}

const isPerson = (value: unknown): value is ChatPerson =>
  typeof value === 'object' && value !== null && typeof (value as Partial<ChatPerson>).id === 'string'

/**
 * A row the chat can show: a message with words (one with no kind, from an API from before cards, is a message), or an
 * `invite`, `joined` or `left` card with the person it is about (its body is empty: the client words it). A kind this
 * client doesn't know is left out, so a card added later never shows as an empty row.
 */
function isChatMessage(value: unknown): value is ChatMessage {
  if (!value || typeof value !== 'object') return false
  const message = value as Partial<ChatMessage>
  const shaped =
    typeof message.id === 'string' &&
    typeof message.body === 'string' &&
    typeof message.created_at === 'string' &&
    isPerson(message.user)
  if (!shaped) return false
  if (message.kind === undefined || message.kind === 'message') return message.body !== '' || isChatGif(message.gif)
  return CARD_KINDS.has(message.kind) && isPerson(message.subject)
}

/** A server message, without any client-only fields it might claim. */
function serverMessage({ pending: _pending, failed: _failed, uncertain: _uncertain, localId: _localId, ...message }: ChatMessage): ChatMessage {
  if (message.gif !== undefined && message.gif !== null && !isChatGif(message.gif)) return { ...message, gif: null }
  return message
}

function parseChatEvent(text: string): ChatEvent | null {
  let event: unknown
  try {
    event = JSON.parse(text)
  } catch {
    return null
  }
  if (!event || typeof event !== 'object') return null
  const { type, message, message_ids: messageIds } = event as { type?: unknown; message?: unknown; message_ids?: unknown }
  if (type === 'chat.message' && isChatMessage(message)) return { type, message: serverMessage(message) }
  if (type === 'chat.removed' && Array.isArray(messageIds)) {
    return { type, message_ids: messageIds.filter((id): id is string => typeof id === 'string') }
  }
  return null
}

/** A block as the API states it, or null when it doesn't stop this viewer now (none, or a timeout already over). */
function blockedFrom(until: string | null | undefined, removed: boolean | undefined): ChatBlocked | null {
  const blocked = { until: until ?? null, removed: Boolean(removed) }
  return chatBlock(blocked).kind === 'none' ? null : blocked
}

/** A failed post the server may have saved anyway: no answer reached the tab, or the server failed (it saves first). */
const mayHaveSaved = (status: number | null) => status === null || status >= 500

let localSequence = 0

/** Per-stage bookkeeping that never renders. */
export interface StageMemory {
  /**
   * Ids already offered to the unread count: a repeat, a confirmation or a restored row never counts again. Pruned to
   * the ids shown, hidden or removed (`pruneStageMemory`).
   */
  seen: Set<string>
  /** Ids a `chat.removed` named: never shown again, from a packet or from history. */
  removed: Set<string>
  /** Ids this tab's moderation hid while its request runs: back if it fails, unless `removed` has them by then. */
  hidden: Set<string>
  /** A tick for each history request and each message from a packet or a post (`arrived`). */
  clock: number
  /**
   * When a message arrived from a packet or a post, so a history page asked for before then doesn't drop it. Entries at
   * or before `applied` protect nothing any more (an older page is stale), nor do those of messages no longer shown or
   * hidden (a page only drops shown ones); both are pruned.
   */
  arrived: Map<string, number>
  /** The newest history request applied: an older one answering later is stale. */
  applied: number
  /** The first history answer or failure has been applied: from then on new packets count as unread. */
  loaded: boolean
  /** A history answer has been applied: later ones count what they add as unread. */
  synced: boolean
}

/** Makes a stage's memory; an object, so a test can watch the memory a chat makes. */
export const stageMemory = {
  fresh: (): StageMemory => ({
    seen: new Set(),
    removed: new Set(),
    hidden: new Set(),
    clock: 0,
    arrived: new Map(),
    applied: 0,
    loaded: false,
    synced: false,
  }),
}

/**
 * Forgets what no longer matters: arrivals a history page covers or whose message is neither shown nor hidden, and
 * seen ids no longer shown, hidden or removed. After each history page, and whenever packets and posts have grown the
 * memory past the shown messages by `STAGE_MEMORY_SLACK`.
 */
export function pruneStageMemory(memory: StageMemory, messages: ChatMessage[]) {
  const shown = new Set(messages.map((m) => m.id))
  const kept = (id: string) => shown.has(id) || memory.hidden.has(id)
  for (const [id, tick] of memory.arrived) if (tick <= memory.applied || !kept(id)) memory.arrived.delete(id)
  for (const id of memory.seen) if (!kept(id) && !memory.removed.has(id)) memory.seen.delete(id)
}

/** After packets or a post: prunes once the memory has outgrown the messages shown by the slack. */
function trimStageMemory(memory: StageMemory, messages: ChatMessage[]) {
  const limit = messages.length + memory.removed.size + memory.hidden.size + STAGE_MEMORY_SLACK
  if (memory.seen.size > limit || memory.arrived.size > limit) pruneStageMemory(memory, messages)
}

interface ChatState {
  stageId: string
  messages: ChatMessage[]
  loading: boolean
  historyError: boolean
  /** Ids of shown messages that arrived while the chat wasn't active. */
  unreadIds: string[]
  draft: string
}

const freshState = (stageId: string): ChatState => ({
  stageId,
  messages: EMPTY,
  loading: true,
  historyError: false,
  unreadIds: [],
  draft: readDraft(stageId),
})

/**
 * A stage's chat (docs/features/watch-experience.md, Realtime). History loads over REST when the stage opens (once
 * the room has connected) and again after every (re)connection, reconciled so what was said or removed while the room
 * was away shows; a failed load retries with backoff until a page loads. Live
 * messages arrive as server data packets on the stage's topic `chat.<stageId>`. Posting and moderation go through the
 * API, which also sends the resulting `chat.message` / `chat.removed` into the room. Must be used inside the room's
 * `RoomContext`.
 *
 * Rules carried over from #228's ChatProvider: messages merge by id, the newest 200 are kept, the draft is kept in
 * sessionStorage per stage (`sb_chat_draft:<stageId>`), and an answer for a stage the viewer has left is dropped.
 */
export function useStageChat(stageId: string, { active, onSignInNeeded, viewer = null }: StageChatOptions): StageChat {
  const room = useRoomContext()
  const { user } = useAuth()

  // Everything shown resets when the stage changes, during render, so another stage's chat never shows for a frame.
  const [state, setState] = useState(() => freshState(stageId))
  let chat = state
  if (state.stageId !== stageId) {
    chat = freshState(stageId)
    setState(chat)
  }

  // The newest state, written synchronously by every change, so async work (a packet, an answer) always builds on the
  // latest messages and can read them back at once. Renders follow it through setState.
  const stateRef = useRef(state)
  const [firstMemory] = useState(stageMemory.fresh)
  const memoryRef = useRef(firstMemory)
  const activeRef = useRef(active)
  const selfIdRef = useRef<string | null>(user?.id ?? null)
  const onSignInNeededRef = useRef(onSignInNeeded)
  useLayoutEffect(() => {
    if (stateRef.current.stageId !== state.stageId) {
      stateRef.current = state
      memoryRef.current = stageMemory.fresh()
    }
    activeRef.current = active
    selfIdRef.current = user?.id ?? null
    onSignInNeededRef.current = onSignInNeeded
  })

  // Seeded from the payload's viewer, again when the stage or the viewer object changes (a reload after an Undo has
  // the same values as before a refused post set it, so the object, not its values, says it's new).
  const [blockState, setBlockState] = useState(() => ({ stageId, viewer, blocked: blockedFrom(viewer?.chat_blocked_until, viewer?.chat_removed) }))
  let blocked = blockState.blocked
  if (blockState.stageId !== stageId || blockState.viewer !== viewer) {
    blocked = blockedFrom(viewer?.chat_blocked_until, viewer?.chat_removed)
    setBlockState({ stageId, viewer, blocked })
  }

  const isCurrent = useCallback((forStage: string) => stateRef.current.stageId === forStage, [])

  /** Applies `change` while `forStage` is the stage on screen. The unread ids follow the messages still shown. */
  const update = useCallback((forStage: string, change: (state: ChatState) => Partial<ChatState>) => {
    const current = stateRef.current
    if (current.stageId !== forStage) return
    const patch = change(current)
    if (Object.keys(patch).length === 0) return
    const next = { ...current, ...patch }
    if (next.messages !== current.messages && next.unreadIds.length > 0) {
      const shown = new Set(next.messages.map((m) => m.id))
      const still = next.unreadIds.filter((id) => shown.has(id))
      if (still.length !== next.unreadIds.length) next.unreadIds = still
    }
    stateRef.current = next
    setState(next)
  }, [])

  const excluded = useCallback((memory: StageMemory): Excluded => ({ has: (id) => memory.removed.has(id) || memory.hidden.has(id) }), [])

  /** The ids among `incoming` that count as unread: new to this stage, someone else's, shown, while the chat is away. */
  const countUnread = useCallback((memory: StageMemory, incoming: ChatMessage[], messages: ChatMessage[], counting: boolean) => {
    const shown = new Set(messages.map((m) => m.id))
    const counted: string[] = []
    for (const message of incoming) {
      if (memory.seen.has(message.id)) continue
      memory.seen.add(message.id)
      if (counting && !activeRef.current && message.user.id !== selfIdRef.current && shown.has(message.id)) counted.push(message.id)
    }
    return counted
  }, [])

  const setDraft = useCallback((text: string) => {
    writeDraft(stageId, text)
    update(stageId, () => ({ draft: text }))
  }, [stageId, update])

  /** Puts a refused message's text back in the draft, unless something new has been typed there. */
  const restoreDraft = useCallback((forStage: string, text: string) => {
    if (readDraft(forStage).trim()) return
    writeDraft(forStage, text)
    update(forStage, (current) => (current.draft.trim() ? {} : { draft: text }))
  }, [update])

  /** Empties the draft if it still holds `body` (now sent, or being sent). */
  const clearDraftIf = useCallback((forStage: string, body: string) => {
    if (chatBody(readDraft(forStage)) === body) writeDraft(forStage, '')
    update(forStage, (current) => (current.draft && chatBody(current.draft) === body ? { draft: '' } : {}))
  }, [update])

  /** Server messages from packets: each confirms this tab's pending (or maybe-saved failed) copy if it has one, then merges by id. */
  const receive = useCallback((forStage: string, incoming: ChatMessage[]) => {
    if (!isCurrent(forStage)) return
    const memory = memoryRef.current
    const fresh = incoming.filter((m) => !excluded(memory).has(m.id) && (!m.stage_id || m.stage_id === forStage))
    if (fresh.length === 0) return
    fresh.forEach((m) => memory.arrived.set(m.id, ++memory.clock))
    let sentAfterAll: string[] = []
    update(forStage, (current) => {
      const claimed = claimPending(current.messages, fresh)
      sentAfterAll = claimed.sentAfterAll
      const messages = mergeMessages(claimed.list, claimed.incoming, excluded(memory))
      const counted = countUnread(memory, fresh, messages, memory.loaded)
      return { messages, ...(counted.length ? { unreadIds: [...current.unreadIds, ...counted] } : {}) }
    })
    trimStageMemory(memory, stateRef.current.messages)
    sentAfterAll.forEach((body) => clearDraftIf(forStage, body))
  }, [isCurrent, update, excluded, countUnread, clearDraftIf])

  /**
   * A history page, asked for at tick `askedAt`, reconciled rather than merged: after its oldest message's millisecond,
   * the page is the server's truth, so a message it doesn't list was removed (or never saved) and goes, unless it
   * arrived after the page was asked for. Older messages and this tab's pending and failed ones stay, and so do those
   * in the oldest's millisecond: the server orders by microseconds, so a page can end between two of them. An empty
   * page clears them all.
   */
  const reconcile = useCallback((forStage: string, page: ChatMessage[], askedAt: number) => {
    if (!isCurrent(forStage)) return
    const memory = memoryRef.current
    if (askedAt < memory.applied) return
    memory.applied = askedAt
    const counting = memory.synced
    memory.synced = true
    memory.loaded = true
    let sentAfterAll: string[] = []
    update(forStage, (current) => {
      const fetched = page.filter((m) => !excluded(memory).has(m.id) && (!m.stage_id || m.stage_id === forStage))
      const listed = new Set(fetched.map((m) => m.id))
      const oldest = page.length > 0 ? Math.min(...page.map(time)) : Number.NEGATIVE_INFINITY
      const kept = current.messages.filter(
        (m) => isLocal(m) || time(m) <= oldest || listed.has(m.id) || (memory.arrived.get(m.id) ?? 0) > askedAt,
      )
      const claimed = claimPending(kept, fetched)
      sentAfterAll = claimed.sentAfterAll
      const merged = mergeMessages(claimed.list, claimed.incoming, excluded(memory))
      const messages = merged.length === current.messages.length && merged.every((m, i) => m === current.messages[i]) ? current.messages : merged
      const counted = countUnread(memory, fetched, messages, counting)
      return { messages, loading: false, historyError: false, ...(counted.length ? { unreadIds: [...current.unreadIds, ...counted] } : {}) }
    })
    pruneStageMemory(memory, stateRef.current.messages)
    sentAfterAll.forEach((body) => clearDraftIf(forStage, body))
  }, [isCurrent, update, excluded, countUnread, clearDraftIf])

  const drop = useCallback((forStage: string, ids: string[]) => {
    if (!isCurrent(forStage)) return
    const memory = memoryRef.current
    ids.forEach((id) => memory.removed.add(id))
    update(forStage, (current) => {
      const messages = current.messages.filter((m) => !ids.includes(m.id))
      return messages.length === current.messages.length ? {} : { messages }
    })
  }, [isCurrent, update])

  /** Asks for this stage's history at once; set while the stage's effect runs. */
  const reloadRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    let live = true
    let jitter: ReturnType<typeof setTimeout> | undefined
    let retry: ReturnType<typeof setTimeout> | undefined
    /** History requests failed in a row; the retry waits longer each time. */
    let failures = 0
    /** The newest history request: it alone decides whether to retry, since any older one's answer is stale by then. */
    let newest = 0
    const topic = `chat.${stageId}`
    const decoder = new TextDecoder()

    const failed = (memory: StageMemory, askedAt: number, error: unknown) => {
      if (askedAt !== newest) return
      memory.loaded = true
      failures += 1
      if (!memory.synced) update(stageId, (current) => (current.loading || !current.historyError ? { loading: false, historyError: true } : {}))
      // Busy: after its Retry-After. Otherwise 2, 5, 15, then every 30 s; either way spread so a room that failed
      // together doesn't come back together.
      const failure = apiFailure(error)
      retry = setTimeout(load, retryDelayMs(failures, { retryAfterMs: failure.status === 429 ? failure.retryAfterMs : null }))
    }

    const load = () => {
      clearTimeout(retry)
      const memory = memoryRef.current
      const askedAt = ++memory.clock
      newest = askedAt
      watchApi.chatHistory(stageId).then(
        (response) => {
          if (!live || memoryRef.current !== memory) return
          // A captive portal's page or a proxy's error body isn't an empty chat: reconciling it would clear the list.
          const data = (response as { data?: unknown } | null | undefined)?.data
          if (!Array.isArray(data)) return failed(memory, askedAt, null)
          if (askedAt === newest) failures = 0
          reconcile(stageId, data.filter(isChatMessage).map(serverMessage), askedAt)
        },
        (error) => {
          if (live && memoryRef.current === memory) failed(memory, askedAt, error)
        },
      )
    }

    // Server packets carry no participant. A packet from a participant is forged: the old viewers' tokens can still
    // publish data into this room.
    const onData = (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, packetTopic?: string) => {
      if (packetTopic !== topic || participant) return
      const event = parseChatEvent(decoder.decode(payload))
      if (event?.type === 'chat.message') receive(stageId, [event.message])
      if (event?.type === 'chat.removed') drop(stageId, event.message_ids)
    }
    // A first connection loads; a token swap's closes the gap since the last load. A reconnection waits a random moment
    // first, so a room full of viewers reconnecting together doesn't ask the API at once.
    const onConnected = () => load()
    const onReconnected = () => {
      clearTimeout(jitter)
      jitter = setTimeout(load, Math.random() * RECONNECT_JITTER_MS)
    }

    room.on(RoomEvent.DataReceived, onData)
    room.on(RoomEvent.Connected, onConnected)
    room.on(RoomEvent.Reconnected, onReconnected)
    reloadRef.current = load
    // Not connected yet (a cold load), `Connected` loads; connected (another stage, say), load now.
    if (room.state === ConnectionState.Connected) load()
    return () => {
      live = false
      reloadRef.current = null
      clearTimeout(jitter)
      clearTimeout(retry)
      room.off(RoomEvent.DataReceived, onData)
      room.off(RoomEvent.Connected, onConnected)
      room.off(RoomEvent.Reconnected, onReconnected)
    }
  }, [room, stageId, receive, reconcile, drop, update])

  const reloadHistory = useCallback(() => {
    // Before any page has loaded, the log goes busy again until this answer (or failure).
    if (!memoryRef.current.synced) update(stateRef.current.stageId, () => ({ loading: true, historyError: false }))
    reloadRef.current?.()
  }, [update])

  useEffect(() => {
    if (active) update(stageId, (current) => (current.unreadIds.length ? { unreadIds: [] } : {}))
  }, [active, stageId, update])

  // A timeout ends on its own; the input opens again then. A removal has no end.
  useEffect(() => {
    if (!blocked || blocked.removed || !blocked.until) return
    const wait = Date.parse(blocked.until) - Date.now()
    if (Number.isNaN(wait) || wait > MAX_TIMER_MS) return
    const clear = () => setBlockState((current) => (current.blocked === blocked ? { ...current, blocked: null } : current))
    if (wait <= 0) {
      clear()
      return
    }
    const timer = setTimeout(clear, wait)
    return () => clearTimeout(timer)
  }, [blocked])

  const deliver = useCallback(async (forStage: string, local: ChatMessage): Promise<boolean> => {
    const withoutLocal = (list: ChatMessage[]) => list.filter((m) => m.id !== local.id)
    try {
      const { data } = await (local.gif ? watchApi.postChat(forStage, local.body, local.gif.id) : watchApi.postChat(forStage, local.body))
      if (!isCurrent(forStage)) return true
      const memory = memoryRef.current
      const message = serverMessage(data)
      memory.seen.add(message.id)
      memory.arrived.set(message.id, ++memory.clock)
      update(forStage, (current) => {
        if (excluded(memory).has(message.id)) return { messages: withoutLocal(current.messages) }
        // Another message took this row's key (a copy from another tab with the same words): keep keys unique.
        const keyTaken = current.messages.some((m) => m.localId === local.id && m.id !== message.id)
        return { messages: mergeMessages(withoutLocal(current.messages), [keyTaken ? message : { ...message, localId: local.id }]) }
      })
      trimStageMemory(memory, stateRef.current.messages)
      if (local.body) clearDraftIf(forStage, local.body)
      return true
    } catch (error) {
      const failure = apiFailure(error)
      const uncertain = mayHaveSaved(failure.status)
      // It may have been saved, and a packet or a history page already confirmed it: the server has it. After a
      // definite refusal, the message that took this row is another copy (the same words from another tab).
      if (uncertain && isCurrent(forStage) && stateRef.current.messages.some((m) => m.localId === local.id && !isLocal(m))) return true
      if (local.body) restoreDraft(forStage, local.body)
      if (!isCurrent(forStage)) return false
      if (failure.status === 401 || failure.type === 'username_required') {
        update(forStage, (current) => ({ messages: withoutLocal(current.messages) }))
        onSignInNeededRef.current(failure.type === 'username_required' ? 'username_required' : 'signed_out')
      } else if (failure.type === 'chat_blocked') {
        update(forStage, (current) => ({ messages: withoutLocal(current.messages) }))
        // A refusal without an end is a removal. A timeout whose end this clock has already passed clears itself.
        const refused: ChatBlocked = failure.removed || !failure.until ? { until: null, removed: true } : { until: failure.until, removed: false }
        setBlockState((current) => ({ ...current, blocked: refused }))
      } else {
        // Failed, and open to a later confirmation when it may have been saved anyway.
        const failedRow: ChatMessage = { ...local, pending: false, failed: true, ...(uncertain ? { uncertain: true } : {}) }
        update(forStage, (current) => ({
          messages: current.messages.some((m) => m.id === local.id)
            ? current.messages.map((m) => (m.id === local.id ? failedRow : m))
            // Another copy took the row but this one wasn't saved: show it failed again, with a key of its own.
            : [...current.messages, { ...failedRow, id: `local-${++localSequence}` }],
        }))
      }
      return false
    }
  }, [isCurrent, update, excluded, clearDraftIf, restoreDraft])

  const isBlocked = chatBlock(blocked).kind !== 'none'

  /** A pending row for this viewer's post on the stage on screen. */
  const localMessage = useCallback((body: string, gif: ChatGif | null): ChatMessage => {
    const attributes = user?.attributes
    return {
      id: `local-${++localSequence}`,
      stage_id: stateRef.current.stageId,
      body,
      gif,
      created_at: new Date().toISOString(),
      user: {
        id: user?.id ?? '',
        handle: attributes?.username ?? null,
        name: attributes?.first_name?.trim() || attributes?.username || null,
        avatar_url: attributes?.avatar_url ?? null,
      },
      pending: true,
    }
  }, [user])

  /** Shows `local` pending, in place of a failed copy of it (sending the same again replaces it), and posts it. */
  const post = useCallback((local: ChatMessage) => {
    const forStage = local.stage_id
    update(forStage, (current) => ({
      messages: [...current.messages.filter((m) => !(m.failed && m.body === local.body && sameGif(m, local))), local],
    }))
    return deliver(forStage, local)
  }, [update, deliver])

  const send = useCallback(async (body?: string) => {
    const text = chatBody(body ?? stateRef.current.draft)
    if (!text || isBlocked) return false
    const local = localMessage(text, null)
    clearDraftIf(local.stage_id, text)
    return post(local)
  }, [isBlocked, localMessage, clearDraftIf, post])

  const sendGif = useCallback(async (gif: ChatGif) => {
    if (isBlocked) return false
    return post(localMessage('', gif))
  }, [isBlocked, localMessage, post])

  const retry = useCallback(async (message: ChatMessage) => {
    const forStage = stateRef.current.stageId
    if (!message.failed || isBlocked) return false
    const { uncertain: _uncertain, ...rest } = message
    const local = { ...rest, pending: true, failed: false }
    update(forStage, (current) => ({ messages: current.messages.map((m) => (m.id === message.id ? local : m)) }))
    if (message.body) clearDraftIf(forStage, message.body)
    return deliver(forStage, local)
  }, [isBlocked, update, clearDraftIf, deliver])

  /** Hides messages at once; the returned undo puts back the ones no `chat.removed` has named since. */
  const hide = useCallback((forStage: string, targets: ChatMessage[]) => {
    const memory = memoryRef.current
    targets.forEach((m) => memory.hidden.add(m.id))
    update(forStage, (current) => ({ messages: current.messages.filter((m) => !targets.includes(m)) }))
    return () => {
      targets.forEach((m) => memory.hidden.delete(m.id))
      if (memoryRef.current !== memory) return
      const back = targets.filter((m) => !memory.removed.has(m.id))
      if (back.length) update(forStage, (current) => ({ messages: mergeMessages(current.messages, back, excluded(memory)) }))
    }
  }, [update, excluded])

  const remove = useCallback(async (messageId: string) => {
    const forStage = stateRef.current.stageId
    const undo = hide(forStage, stateRef.current.messages.filter((m) => m.id === messageId))
    try {
      await watchApi.deleteChat(forStage, messageId)
    } catch (error) {
      undo()
      throw error
    }
  }, [hide])

  const ban = useCallback(async (userId: string, kind: StageBanKind): Promise<StageChatBan> => {
    const forStage = stateRef.current.stageId
    // The person's messages, as the server hides them; their invite, joined and left cards stay, so the chat still says what happened.
    const undo = hide(forStage, stateRef.current.messages.filter((m) => m.user.id === userId && !isLocal(m) && !isChatCard(m)))
    try {
      const { data } = await watchApi.ban(forStage, userId, kind)
      return { ...data, stage_id: forStage }
    } catch (error) {
      undo()
      throw error
    }
  }, [hide])

  const unban = useCallback(async (userId: string, onStage?: string) => {
    await watchApi.unban(onStage ?? stateRef.current.stageId, userId)
  }, [])

  return {
    messages: chat.messages,
    loading: chat.loading,
    historyError: chat.historyError,
    reloadHistory,
    draft: chat.draft,
    setDraft,
    send,
    sendGif,
    retry,
    blocked,
    unread: active ? 0 : chat.unreadIds.length,
    remove,
    ban,
    unban,
  }
}
